<template>
  <div class="bank-id-sweden-container">
    <div v-if="showStatus" class="status-container">
      <EidStatus :title="userMessage.title" :description="userMessage.description">
        <template v-if="state !== 'cancelling'" #button>
          <FriedLink @click="handleCancel">
            {{ t('cancel') }}
          </FriedLink>
        </template>
      </EidStatus>
    </div>
    <div v-else class="start-container">
      <TransitionFadeInOut>
        <FriedMessage v-if="failedReason" class="message" type="error" :title="failedReason.title">
          {{ failedReason.description }}
        </FriedMessage>
      </TransitionFadeInOut>
      <div class="sections">
        <template v-if="uiState === 'start-desktop'">
          <FriedH4>{{ t('cr_scan_qr_code') }}</FriedH4>
          <BankIdQrCode :qr-code-url="qrCodeUrl" />
          <OrDivider class="divider" />
          <BankIdOpenAppButton :app-url="appUrl" />
        </template>
        <template v-else-if="uiState === 'start-mobile'">
          <BankIdOpenAppButton :app-url="appUrl" />
          <OrDivider class="divider" />
          <FriedButton
            button-type="secondary"
            data-external="bank-id-sweden-show-qr-code-button"
            @click="uiState = 'qr-code-mobile'"
          >
            {{ t('cr_show_qr_code') }}
          </FriedButton>
        </template>
        <template v-else-if="uiState === 'qr-code-mobile'">
          <FriedH4>{{ t('cr_scan_qr_code') }}</FriedH4>
          <BankIdQrCode :qr-code-url="qrCodeUrl" is-mobile-view />
          <FriedLink class="qr-code-cancel-button" @click="uiState = 'start-mobile'">
            {{ t('cancel') }}
          </FriedLink>
        </template>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import type { Ref } from 'vue';
import { computed, watch, ref } from 'vue';
import QRCode from 'qrcode';
import { computedAsync } from '@vueuse/core';
import useBreakpoints from '@getaccept/lib-shared/src/composables/breakpoints.composable';
import { useUserAgent } from '@getaccept/lib-shared/src/composables/user-agent.composable';
import { SigningHelper } from '../../helpers/signing.helper';
import { useBankIdSweden } from '../composables/bank-id-sweden.composable';
import { useRootStore } from '../../../store/root.store';
import { BankIdSwedenHelper } from '../helpers/bank-id-sweden.helper';
import type { UserMessage } from '../types/bank-id-sweden';
import { useDocumentStore } from '../../../documents/store/document.store';
import { useResumeEidSigning } from '../composables/resume-eid-signing.composable';
import OrDivider from './OrDivider.vue';
import EidStatus from './EidStatus.vue';
import BankIdQrCode from './BankIdQrCode.vue';
import BankIdOpenAppButton from './BankIdOpenAppButton.vue';

const emit = defineEmits(['sign']);

const { t } = useI18n();
const rootStore = useRootStore();
const documentStore = useDocumentStore();
const { bankIdSwedenOrderRef, bankIdSwedenAutoStartToken } = useResumeEidSigning();
const { isXSmallScreenAndDown } = useBreakpoints();
const { isMobileOrTabletDevice } = useUserAgent();

const signingAttempted = ref(false);
const failedReason: Ref<UserMessage> = ref(null);
const uiState = ref<'qr-code-mobile' | 'start-mobile' | 'start-desktop'>();

const { state, statusCode, qrCode, appUrl, start, cancel, resume } = useBankIdSweden({
  documentId: documentStore.document.id,
  recipientId: rootStore.recipientId,
});

const showMobileView = computed(() => isMobileOrTabletDevice.value || isXSmallScreenAndDown.value);

const userMessage = computed(() =>
  BankIdSwedenHelper.getUserMessage(state.value, statusCode.value)
);

const showStatus = computed(() => ['signing', 'cancelling'].includes(state.value));

const isDocumentSigned = async () => {
  await documentStore.loadRecipients();
  return SigningHelper.isSignedByRecipient(
    documentStore.document.recipients,
    rootStore.recipientId
  );
};

const handleFailed = async () => {
  if (signingAttempted.value) {
    const isSigned = await isDocumentSigned();
    if (isSigned) {
      emit('sign');
      return;
    }
  }

  failedReason.value = userMessage.value;
  start();
};

watch(
  state,
  () => {
    switch (state.value) {
      case 'not_started':
        signingAttempted.value = false;
        break;
      case 'signing':
        failedReason.value = null;
        signingAttempted.value = true;
        break;
      case 'failed':
        handleFailed();
        break;
      case 'completed':
        emit('sign');
        break;
    }
  },
  { immediate: true }
);

const handleCancel = () => {
  cancel();
  start();
};

watch(
  showMobileView,
  () => {
    uiState.value = showMobileView.value ? 'start-mobile' : 'start-desktop';
  },
  { immediate: true }
);

const init = () => {
  if (bankIdSwedenOrderRef.value) {
    resume(bankIdSwedenOrderRef.value, bankIdSwedenAutoStartToken.value);
  } else {
    start();
  }
};

const qrCodeUrl = computedAsync<string>(async () => {
  try {
    return await QRCode.toDataURL(qrCode.value, { margin: 0 });
  } catch (error) {
    return '';
  }
}, null);

init();
</script>
<style lang="scss" scoped>
.bank-id-sweden-container {
  .status-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    min-height: 238px;
  }

  .start-container {
    .message {
      margin: var(--spacing-50) 0 var(--spacing-150);
    }

    .sections {
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      gap: var(--spacing-150);

      .qr-code-cancel-button {
        margin: auto;
      }
    }
  }
}
</style>
